import { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { User, Lock, Loader2, Camera, Moon, Sun } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { useAuthStore } from '../stores/auth.store';
import { authApi } from '../services/auth.service';
import { useTheme } from '../components/theme-provider';
import { tenantConfig } from '../lib/tenant';

export default function UserSettingsPage() {
  const { user, updateUser } = useAuthStore();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const isWhitelabel = tenantConfig.id !== 'default';

  // ─── Perfil ───────────────────────────────────────────────────────────────
  const [name, setName] = useState(user?.name ?? '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatarUrl ?? null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast({ title: 'Imagem muito grande', description: 'Tamanho máximo: 4 MB', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target?.result as string;
      setAvatarPreview(base64);
      avatarMutation.mutate(base64);
    };
    reader.readAsDataURL(file);
  };

  const avatarMutation = useMutation({
    mutationFn: (avatarUrl: string) => authApi.updateMe({ avatarUrl }),
    onSuccess: (data) => {
      updateUser({ avatarUrl: data.avatarUrl ?? undefined });
      toast({ title: 'Foto atualizada!' });
    },
    onError: (err: any) => {
      setAvatarPreview(user?.avatarUrl ?? null);
      toast({ title: 'Erro ao atualizar foto', description: err.response?.data?.message ?? err.message, variant: 'destructive' });
    },
  });

  const profileMutation = useMutation({
    mutationFn: () => authApi.updateMe({ name: name.trim() }),
    onSuccess: (data) => {
      updateUser({ name: data.name ?? undefined });
      toast({ title: 'Perfil atualizado com sucesso!' });
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao atualizar perfil',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
    },
  });

  // ─── Senha ─────────────────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const passwordMutation = useMutation({
    mutationFn: () =>
      authApi.updateMe({ currentPassword, newPassword }),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast({ title: 'Senha alterada com sucesso!' });
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao alterar senha',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
    },
  });

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ title: 'As senhas não coincidem', variant: 'destructive' });
      return;
    }
    passwordMutation.mutate();
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Configurações da conta</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gerencie suas informações pessoais e segurança.
        </p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" className="gap-2">
            <User className="h-4 w-4" />
            Perfil
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Lock className="h-4 w-4" />
            Segurança
          </TabsTrigger>
        </TabsList>

        {/* ─── Perfil ─── */}
        <TabsContent value="profile" className="mt-6">
          <div className="rounded-lg border bg-card p-6 space-y-6">
            <div>
              <h2 className="text-base font-semibold">Informações pessoais</h2>
              <p className="text-sm text-muted-foreground">Atualize seu nome de exibição.</p>
            </div>

            {/* Avatar */}
            <div className="flex items-center gap-4">
              <div className="relative group">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-semibold text-primary overflow-hidden">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="avatar" className="w-full h-full object-cover" />
                  ) : (
                    user?.name
                      ? user.name.charAt(0).toUpperCase()
                      : user?.email?.charAt(0).toUpperCase() ?? 'U'
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarMutation.isPending}
                  className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                  title="Alterar foto"
                >
                  {avatarMutation.isPending
                    ? <Loader2 className="h-5 w-5 text-white animate-spin" />
                    : <Camera className="h-5 w-5 text-white" />}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </div>
              <div>
                <p className="font-medium">{user?.name || '—'}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Clique na foto para alterar (máx. 4 MB)</p>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                profileMutation.mutate();
              }}
              className="space-y-4"
            >
              <div className="space-y-1.5">
                <Label htmlFor="user-name">Nome</Label>
                <Input
                  id="user-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="user-email">E-mail</Label>
                <Input id="user-email" value={user?.email ?? ''} disabled readOnly />
                <p className="text-xs text-muted-foreground">O e-mail não pode ser alterado.</p>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={profileMutation.isPending}>
                  {profileMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Salvar alterações
                </Button>
              </div>
            </form>
          </div>
        </TabsContent>

        {/* ─── Aparência (apenas whitelabel) ─── */}
        {isWhitelabel && (
          <div className="rounded-lg border bg-card p-6 space-y-4 mt-6">
            <div>
              <h2 className="text-base font-semibold">Aparência</h2>
              <p className="text-sm text-muted-foreground">Escolha entre o tema claro ou escuro.</p>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isDark ? <Moon className="h-5 w-5 text-muted-foreground" /> : <Sun className="h-5 w-5 text-muted-foreground" />}
                <span className="text-sm font-medium">{isDark ? 'Tema escuro' : 'Tema claro'}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isDark}
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  isDark ? 'bg-primary' : 'bg-muted-foreground/30'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    isDark ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {/* ─── Segurança ─── */}
        <TabsContent value="security" className="mt-6">
          <div className="rounded-lg border bg-card p-6 space-y-6">
            <div>
              <h2 className="text-base font-semibold">Alterar senha</h2>
              <p className="text-sm text-muted-foreground">
                Escolha uma senha segura com pelo menos 6 caracteres.
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="current-password">Senha atual</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="new-password">Nova senha</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirmar nova senha</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={passwordMutation.isPending || !currentPassword || !newPassword || !confirmPassword}
                >
                  {passwordMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Alterar senha
                </Button>
              </div>
            </form>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
